import SEOHead from "@/components/SEOHead";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import ServiceNavigation from "@/components/ServiceNavigation";
import { motion } from "framer-motion";
import { GraduationCap, Check, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useState, useEffect } from "react";
import LeadFormModal from "@/components/LeadFormModal";

const features = [
  { text: "Accompagnement sur le format, le ton et la ligne éditoriale" },
  { text: "Captation (studio mobile ou en entreprise)" },
  { text: "Montage audio & vidéo professionnel" },
  { text: "Structuration pédagogique claire" },
  { text: "Intégration plateforme LMS" },
  { text: "Stratégie de lancement et promotion" },
];

const processSteps = [
  { icon: "🎯", title: "Format & Ligne éditoriale", desc: "Accompagnement sur le format, le ton et la stratégie de contenu" },
  { icon: "🎥", title: "Captation professionnelle", desc: "Studio mobile ou tournage en entreprise selon vos besoins" },
  { icon: "🎛️", title: "Montage audio & vidéo", desc: "Post-production professionnelle pour un rendu premium" },
  { icon: "📚", title: "Structure pédagogique", desc: "Organisation claire et progression logique du contenu" },
  { icon: "💻", title: "Intégration LMS", desc: "Mise en ligne sur votre plateforme de formation" },
  { icon: "🚀", title: "Lancement & Promotion", desc: "Stratégie marketing pour maximiser vos inscriptions" },
];

const formationsSchema = {
  "@context": "https://schema.org",
  "@type": "Service",
  "name": "Production de formations en ligne",
  "serviceType": "Production de contenu e-learning",
  "description": "Production complète de formations en ligne pour entreprises : captation vidéo, montage professionnel, structuration pédagogique et intégration sur plateformes LMS.",
  "provider": {
    "@type": "LocalBusiness",
    "name": "Agence 8020",
    "address": { "@type": "PostalAddress", "addressLocality": "Montréal", "addressRegion": "QC", "addressCountry": "CA" },
    "url": "https://agence8020.com"
  },
  "areaServed": { "@type": "Place", "name": "Québec, Canada" }
};

const FormationsPage = () => {
  const [isLeadModalOpen, setIsLeadModalOpen] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <SEOHead
        title="Production de formations en ligne pour entreprises | Agence 8020 Montréal"
        description="Production professionnelle de formations en ligne pour entreprises au Québec. Captation vidéo, montage, structuration pédagogique et intégration LMS pour monétiser votre expertise."
        canonical="/formations"
        schemaJson={formationsSchema}
      />

      <main className="min-h-screen bg-background overflow-hidden">
        <Navbar />
        
        {/* Hero Section */}
        <section className="pt-32 pb-8 relative">
          <div className="absolute inset-0 overflow-hidden">
            <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[1000px] h-[600px] bg-gradient-to-b from-primary/20 to-transparent opacity-50 blur-[120px] rounded-full" />
          </div>
          
          <div className="container mx-auto px-6 relative z-10">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="text-center"
            >
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 border border-primary/20 mb-6">
                <GraduationCap className="w-4 h-4 text-primary" />
                <span className="text-sm font-medium text-primary">E-Learning</span>
              </div>
              <h1 className="font-heading text-5xl md:text-7xl lg:text-8xl font-bold tracking-tight mb-6">
                Formations <br className="hidden md:block" />
                <span className="text-primary">en ligne</span>
              </h1>
              <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
                Transformez votre expertise en revenus récurrents avec des formations professionnelles.
              </p>
            </motion.div>
          </div>
        </section>

        <ServiceNavigation />

        {/* Hero Content Section */}
        <section className="py-16">
          <div className="container mx-auto px-6">
            <div className="grid lg:grid-cols-2 gap-12 items-center max-w-6xl mx-auto">
              {/* Left Content */}
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
              >
                <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold mb-6">
                  Des <span className="text-primary">formations en ligne</span> professionnelles et rentables.
                </h2>
                <p className="text-muted-foreground text-lg leading-relaxed mb-8">
                  Vous avez une expertise à transmettre? Transformez-la en une formation structurée, crédible et monétisable.
                </p>
                <p className="text-foreground text-lg leading-relaxed">
                  Notre équipe vous accompagne à chaque étape : de la planification pédagogique jusqu'à la mise en ligne et la promotion.
                </p>
              </motion.div>

              {/* Right Image */}
              <motion.div
                initial={{ opacity: 0, x: 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="relative"
              >
                <div className="relative rounded-3xl overflow-hidden aspect-[4/3]">
                  <img 
                    src="https://images.unsplash.com/photo-1522202176988-66273c2fd55f?w=800&q=80" 
                    alt="Formation en ligne professionnelle"
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-primary/30 to-transparent" />
                </div>
                <div className="absolute -top-4 -right-4 w-24 h-24 bg-primary/20 rounded-full blur-2xl" />
                <div className="absolute -bottom-4 -left-4 w-32 h-32 bg-primary/20 rounded-full blur-2xl" />
              </motion.div>
            </div>
          </div>
        </section>

        {/* Process Section */}
        <section className="py-16">
          <div className="container mx-auto px-6">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <h3 className="font-heading text-2xl md:text-3xl font-bold text-center mb-12">
                Ce que nous faisons pour <span className="text-primary">vous</span>
              </h3>
              
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
                {processSteps.map((item, index) => (
                  <motion.div
                    key={item.title}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 }}
                    className="p-6 rounded-2xl bg-card/50 border border-border/50 hover:border-primary/30 hover:bg-card/80 transition-all duration-300 group"
                    whileHover={{ scale: 1.02, y: -4 }}
                  >
                    <span className="text-3xl mb-4 block">{item.icon}</span>
                    <h4 className="font-heading text-lg font-bold mb-2 text-foreground group-hover:text-primary transition-colors">{item.title}</h4>
                    <p className="text-muted-foreground text-sm">{item.desc}</p>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>
        </section>

        {/* Features Section */}
        <section className="py-16">
          <div className="container mx-auto px-6">
            <div className="max-w-4xl mx-auto text-center mb-16">
              <motion.h2
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold mb-6"
              >
                Transformez votre expertise en revenus <span className="text-primary">récurrents.</span>
              </motion.h2>
            </div>

            {/* Features Grid */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 max-w-5xl mx-auto mb-16"
            >
              {features.map((feature, index) => (
                <motion.div
                  key={feature.text}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="flex items-center gap-4 p-5 rounded-2xl bg-card/50 border border-border/50 hover:border-primary/30 hover:bg-card/80 transition-all duration-300 group"
                  whileHover={{ scale: 1.02, y: -2 }}
                >
                  <Check className="w-5 h-5 text-primary flex-shrink-0" />
                  <span className="text-foreground font-medium">{feature.text}</span>
                </motion.div>
              ))}
            </motion.div>

            {/* Highlight Box */}
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              className="max-w-3xl mx-auto"
            >
              <div className="p-8 rounded-3xl bg-primary/10 relative overflow-hidden border border-primary/20">
                <div className="absolute inset-0 bg-background/80" />
                <div className="relative z-10 flex items-start gap-4">
                  <div className="w-12 h-12 rounded-2xl bg-primary flex items-center justify-center flex-shrink-0">
                    <Sparkles className="w-6 h-6 text-primary-foreground" />
                  </div>
                  <p className="text-lg text-foreground leading-relaxed">
                    Que vous souhaitiez tourner dans notre studio ou dans votre propre environnement, nous vous accompagnons pour créer une formation de qualité qui génère des revenus récurrents.
                  </p>
                </div>
              </div>
            </motion.div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 relative">
          <div className="absolute inset-0 bg-gradient-to-t from-primary/5 via-transparent to-transparent" />
          <div className="container mx-auto px-6 relative z-10 text-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="max-w-2xl mx-auto"
            >
              <h2 className="font-heading text-3xl md:text-4xl font-bold mb-4">
                Vous voulez créer une <span className="text-primary">formation</span> rentable?
              </h2>
              <p className="text-muted-foreground text-lg mb-8">
                Parlez-nous de votre projet. On vous répond en 24h.
              </p>
              <Button 
                variant="hero" 
                size="xl" 
                className="shadow-neon group"
                onClick={() => setIsLeadModalOpen(true)}
              >
                Parlons de votre projet
                <motion.span animate={{ x: [0, 5, 0] }} transition={{ repeat: Infinity, duration: 1.5 }}>→</motion.span>
              </Button>
            </motion.div>
          </div>
        </section>

        <LeadFormModal isOpen={isLeadModalOpen} onClose={() => setIsLeadModalOpen(false)} />
        <Footer />
      </main>
    </>
  );
};

export default FormationsPage;